/**
 * Shared folder tree utilities for Slide Sense.
 *
 * Used by FolderNav, SlideSenseSidebar, and AdminFileTree.
 */

import type { SlideSenseFolder } from "../types"

export interface FolderNode {
  folder: SlideSenseFolder
  children: FolderNode[]
}

export type FolderSortMode = "manual" | "alphabetical" | "updated"

function compareFolders(a: SlideSenseFolder, b: SlideSenseFolder, mode: FolderSortMode): number {
  if (mode === "alphabetical") return a.name.localeCompare(b.name)
  if (mode === "updated") {
    const aTime = a.updated_at ?? a.created_at
    const bTime = b.updated_at ?? b.created_at
    return bTime.localeCompare(aTime) // newest first
  }
  return a.sort_order - b.sort_order
}

/**
 * Build a recursive tree from a flat folder list using `parent_folder_id`.
 *
 * `sortMode` controls sibling ordering at every level: "manual" uses the
 * stored `sort_order`, "alphabetical" sorts by name, "updated" sorts by
 * `updated_at` desc.
 */
export function buildTree(
  folders: SlideSenseFolder[],
  sortMode: FolderSortMode = "manual"
): FolderNode[] {
  // Filter out self-referencing folders to prevent infinite recursion
  const safe = folders.filter((f) => f.parent_folder_id !== f.id)
  const sorted = [...safe].sort((a, b) => compareFolders(a, b, sortMode))
  const childrenMap = new Map<string | null, SlideSenseFolder[]>()
  for (const f of sorted) {
    const key = f.parent_folder_id
    if (!childrenMap.has(key)) childrenMap.set(key, [])
    childrenMap.get(key)!.push(f)
  }
  const visited = new Set<string>()
  function buildNodes(parentId: string | null): FolderNode[] {
    return (childrenMap.get(parentId) ?? [])
      .filter((f) => !visited.has(f.id)) // circular reference guard
      .map((folder) => {
        visited.add(folder.id)
        return { folder, children: buildNodes(folder.id) }
      })
  }
  return buildNodes(null)
}

/**
 * Build a map from folder id -> parent folder id for ancestor lookups.
 */
export function buildParentMap(folders: SlideSenseFolder[]): Map<string, string | null> {
  const map = new Map<string, string | null>()
  for (const f of folders) map.set(f.id, f.parent_folder_id)
  return map
}

/**
 * Collect all ancestor folder ids for a given folder id (excluding itself).
 */
export function getAncestorIds(
  folderId: string | null,
  parentMap: Map<string, string | null>
): Set<string> {
  const ancestors = new Set<string>()
  if (!folderId) return ancestors
  let current = parentMap.get(folderId) ?? null
  while (current !== null) {
    if (ancestors.has(current)) break // circular reference guard
    ancestors.add(current)
    current = parentMap.get(current) ?? null
  }
  return ancestors
}

/**
 * Check whether `targetId` is a descendant of `ancestorId` in the tree.
 */
export function isDescendantOf(
  targetId: string | null,
  ancestorId: string,
  parentMap: Map<string, string | null>
): boolean {
  if (!targetId) return false
  const visited = new Set<string>()
  let current: string | null = targetId
  while (current !== null) {
    if (current === ancestorId) return true
    if (visited.has(current)) break // circular reference guard
    visited.add(current)
    current = parentMap.get(current) ?? null
  }
  return false
}
